<template>
  <figure class="viz-card">
    <figcaption class="viz-header">
      <h3>{{ title }}</h3>
      <span v-if="note" class="viz-note">{{ note }}</span>
    </figcaption>

    <div v-if="loading" class="viz-empty">Loading…</div>
    <div v-else-if="!buckets.length" class="viz-empty">{{ emptyText }}</div>

    <!-- Always mounted (v-show, not v-if/v-else) - see BarChart.vue for why. -->
    <apexchart
      v-show="!loading && buckets.length"
      type="line"
      height="360"
      class="apex-clickable"
      :options="chartOptions"
      :series="chartSeries"
      @markerClick="onMarkerClick"
      @dataPointSelection="onPointSelection"
    />
  </figure>
</template>

<script>
import { PALETTE, EMPHASIS_COLORS, MUTED_LINE_COLOR } from './chartPalette.js';

export default {
  name: 'SessionProgressChart',
  props: {
    title: { type: String, required: true },
    // [{ key, label, logbook_id, behind, ahead, isTarget }] - one entry per
    // trainee, plus one `isTarget: true` entry for the expected-pace
    // reference line. `ahead` covers "Ahead of pace"/"Pass rate met"/
    // "Complete" - trainees who don't need chasing, same as `behind` covers
    // just "Behind pace".
    series: { type: Array, default: () => [] },
    buckets: { type: Array, default: () => [] }, // [{ label, [seriesKey]: pct 0-100 }]
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'No data available.' },
    note: { type: String, default: '' },
  },
  emits: ['point-click'],
  computed: {
    // Behind-pace trainees get a distinct color each, cycling through
    // EMPHASIS_COLORS in the order they appear (not the order among ALL
    // series), so the first flagged trainee is always red, the second
    // orange, etc., regardless of how many on-track trainees sit before them.
    behindColorIndex() {
      const map = new Map();
      let i = 0;
      this.series.forEach((s) => {
        if (s.behind) {
          map.set(s.key, i);
          i += 1;
        }
      });
      return map;
    },
    chartSeries() {
      return this.series.map((s) => ({
        name: s.label,
        data: this.buckets.map((b) => b[s.key] ?? 0),
      }));
    },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          animations: { easing: 'easeout', speed: 300 },
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        },
        colors: this.series.map((s) => this.colorFor(s)),
        stroke: {
          curve: 'smooth',
          width: this.series.map((s) => (s.isTarget ? 2 : s.behind ? 2.5 : 1.5)),
          dashArray: this.series.map((s) => (s.isTarget ? 6 : 0)),
        },
        markers: { size: 0, hover: { size: 5 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.buckets.map((b) => b.label),
          labels: { style: { colors: PALETTE.textMuted, fontSize: '10px' } },
          axisBorder: { color: PALETTE.gridline },
          axisTicks: { color: PALETTE.gridline },
        },
        yaxis: {
          min: 0,
          max: 100,
          labels: { style: { colors: PALETTE.textMuted, fontSize: '11px' }, formatter: (v) => `${Math.round(v)}%` },
        },
        grid: { borderColor: PALETTE.gridline },
        legend: {
          position: 'bottom',
          fontSize: '12px',
          markers: { size: 6 },
          itemMargin: { horizontal: 8, vertical: 4 },
        },
        tooltip: {
          shared: false,
          intersect: true,
          y: { formatter: (v) => `${v}%` },
        },
      };
    },
  },
  methods: {
    colorFor(s) {
      if (s.isTarget) return PALETTE.textSecondary;
      if (s.behind) return EMPHASIS_COLORS[this.behindColorIndex.get(s.key) % EMPHASIS_COLORS.length];
      // Ahead/passed trainees get one flat green, not individually-distinct
      // colors like `behind` does - they don't need chasing up individually,
      // just a reassuring "this group is fine" signal against the muted grey
      // "in progress"/"on track" pack.
      if (s.ahead) return PALETTE.good;
      return MUTED_LINE_COLOR;
    },
    emitPointClick(seriesIndex, dataPointIndex) {
      const seriesDef = this.series[seriesIndex];
      const bucket = this.buckets[dataPointIndex];
      if (!seriesDef || !bucket || seriesDef.isTarget) return;
      this.$emit('point-click', { series: seriesDef, bucket });
    },
    onMarkerClick(event, chartContext, { seriesIndex, dataPointIndex }) {
      this.emitPointClick(seriesIndex, dataPointIndex);
    },
    onPointSelection(event, chartContext, config) {
      this.emitPointClick(config.seriesIndex, config.dataPointIndex);
    },
  },
};
</script>

<style scoped>
.viz-card {
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 18px 20px 20px;
  margin: 0;
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
}

.viz-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--gridline);
}

.viz-header h3 {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.viz-note {
  font-size: 12px;
  color: var(--text-muted);
}

.viz-empty {
  font-size: 13px;
  color: var(--text-muted);
  padding: 24px 0;
  text-align: center;
}

.apex-clickable :deep(.apexcharts-marker) {
  cursor: pointer;
}
</style>
